import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  HARDWARE_CHECK_KINDS,
  hardwareCheckRow,
  inverterIsolatorOfferView,
  selectedInverterIsolatorFields,
  type FieldHardware,
  type HardwareCheck,
  type HardwareCheckSelection,
  type StoredPhotoManifest,
  type StoredReport,
} from '@cer-autofill/shared';
import { ApiError, apiErrorReason, fetchPhotoBlobUrl, requestHardwareCheck } from '../api.js';
import { LabelPhotoCrop } from './LabelPhotoCrop.js';
import { HardwareCheckFields, InverterIsolatorOffers } from './HardwareCheckFields.js';
import {
  AUTO_HARDWARE_CHECK_CONCURRENCY,
  CHOOSE_PHOTO_FOR_EACH_UNIT,
  NOT_CHECKED_AUTOMATICALLY,
  applyHardwareCheckSelections,
  autoCheckProgressLabel,
  autoCheckShouldStop,
  autoHardwareChecksAllowed,
  dismissHardwareReview,
  hardwareCheckRequestKey,
  acceptOnlyReviewsKey,
  hardwareCheckResultCurrent,
  hardwareReadingTicks,
  pendingAcceptOnlySuggestions,
  planAutoChecks,
  runAutoHardwareChecks,
  selectedHardwareFields,
  shareInFlight,
  undoHardwareReviewDismiss,
  type AcceptOnlyReview,
  type PlannedHardwareCheck,
  type SkippedHardwareCheck,
} from '../lib/auto-hardware-checks.js';

function photoName(photos: readonly StoredPhotoManifest[], photoId: string): string {
  return photos.find((photo) => photo.photoId === photoId)?.filename ?? 'Photo';
}

function checkKey(item: { kind: string; photoId: string; surface?: string | undefined }): string {
  return `${item.surface ?? 'equipment'}:${item.kind}:${item.photoId}`;
}

function readingRow(
  hardware: FieldHardware,
  result: HardwareCheck,
): Record<string, unknown> | undefined {
  if (result.surface === 'switchboard')
    return {
      circuitBreakerSizeMainSwitchGrid: hardware.circuitBreakerSizeMainSwitchGrid,
      circuitBreakerSizeMainSwitchInverter: hardware.circuitBreakerSizeMainSwitchInverter,
    };
  return hardwareCheckRow(hardware, result.kind, result.index);
}

/**
 * Starts the photo checks for this edit. The editor is already open; nothing
 * here is applied until the reviewer chooses to.
 */
export function AutoHardwareChecks({
  report,
  hardware,
  photos,
  disabled = false,
  dismissedIds = [],
  onDismissedIdsChange,
  knownResults,
  onApply,
  onSeedEmptyBattery,
  onAcceptOnlyReviews,
}: {
  report: Pick<StoredReport, 'jobId' | 'submittedAt' | 'status'>;
  hardware: FieldHardware;
  photos: readonly StoredPhotoManifest[];
  disabled?: boolean;
  /** Photo reviews the reviewer dismissed. Persisted with the edit draft. */
  dismissedIds?: readonly string[];
  onDismissedIdsChange?: (ids: string[]) => void;
  /**
   * Checks already in hand, so the list can render without waiting for the
   * network. The editor leaves this unset and lets the effect run.
   */
  knownResults?: readonly HardwareCheck[];
  onApply: (hardware: FieldHardware, selections: HardwareCheckSelection[]) => void;
  /** Write Battery 1 into the draft after a battery photo check succeeds. */
  onSeedEmptyBattery?: () => void;
  onAcceptOnlyReviews?: (reviews: readonly AcceptOnlyReview[]) => void;
}) {
  const session = useRef<{
    hardware: FieldHardware;
    plan: ReturnType<typeof planAutoChecks>;
    allowed: boolean;
  } | null>(null);
  const photoSignature = photos
    .map((photo) => `${photo.photoId}:${photo.promptKey ?? ''}:${photo.uploadedAt}`)
    .join('|');
  if (session.current === null) {
    session.current = {
      hardware,
      plan: planAutoChecks(hardware, photos),
      allowed: autoHardwareChecksAllowed(report.status),
    };
  }
  const hardwareNow = useRef(hardware);
  hardwareNow.current = hardware;
  const seedBattery = useRef(onSeedEmptyBattery);
  seedBattery.current = onSeedEmptyBattery;
  const [plan, setPlan] = useState(session.current.plan);
  const { allowed } = session.current;
  useEffect(() => {
    const next = planAutoChecks(hardwareNow.current, photos);
    setPlan((current) => {
      const known = new Set(current.checks.map(checkKey));
      const added = next.checks.filter((item) => !known.has(checkKey(item)));
      if (!added.length && next.checks.length === current.checks.length) return current;
      return {
        checks: [...current.checks, ...added],
        skipped: next.skipped,
      };
    });
  }, [photoSignature, photos]);
  const [done, setDone] = useState(0);
  const [phase, setPhase] = useState<'checking' | 'done'>(
    knownResults || !allowed || plan.checks.length === 0 ? 'done' : 'checking',
  );
  const [results, setResults] = useState<HardwareCheck[]>(() => [...(knownResults ?? [])]);
  const [failures, setFailures] = useState<{ key: string; message: string }[]>([]);
  const [stoppedMessage, setStoppedMessage] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [acceptedRows, setAcceptedRows] = useState<Record<string, Record<string, unknown>>>({});
  const [overrides, setOverrides] = useState<Record<string, Record<string, boolean>>>({});
  const [isolatorOverrides, setIsolatorOverrides] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [isolatorTargets, setIsolatorTargets] = useState<Record<string, number>>({});
  const [imagesReady, setImagesReady] = useState<Record<string, boolean>>(() =>
    Object.fromEntries((knownResults ?? []).map((result) => [result.id, true])),
  );

  const startedChecks = useRef(new Set<string>());
  useEffect(() => {
    if (knownResults || !allowed || plan.checks.length === 0) return;
    const pending = plan.checks.filter((item) => !startedChecks.current.has(checkKey(item)));
    if (!pending.length) return;
    for (const item of pending) startedChecks.current.add(checkKey(item));
    setPhase('checking');
    void runAutoHardwareChecks({
      items: pending,
      concurrency: AUTO_HARDWARE_CHECK_CONCURRENCY,
      shouldQueue: () => true,
      request: (item) => {
        const request = {
          kind: item.kind,
          index: item.index,
          photoId: item.photoId,
          hardware: hardwareNow.current,
          ...(item.surface ? { surface: item.surface } : {}),
        };
        return shareInFlight(
          hardwareCheckRequestKey({
            jobId: report.jobId,
            submittedAt: report.submittedAt,
            request,
          }),
          () => requestHardwareCheck(report.jobId, report.submittedAt, request),
        );
      },
      classifyError: (error) => ({
        status: error instanceof ApiError ? error.status : undefined,
        message: apiErrorReason(error),
      }),
      onResult: (_item, result) => {
        if (result.kind === 'batteries') seedBattery.current?.();
        setResults((current) =>
          current.some((existing) => existing.id === result.id) ? current : [...current, result],
        );
      },
      onError: (item, error) => {
        if (autoCheckShouldStop(error)) {
          setStoppedMessage(error.message);
          return;
        }
        const key = checkKey(item);
        setFailures((current) =>
          current.some((failure) => failure.key === key)
            ? current
            : [...current, { key, message: error.message }],
        );
      },
      onProgress: (completed) => {
        setDone(completed);
      },
    }).finally(() => {
      setPhase('done');
    });
  }, [allowed, knownResults, plan, report.jobId, report.submittedAt]);

  const onImageReady = useCallback((id: string, ready: boolean) => {
    setImagesReady((current) => (current[id] === ready ? current : { ...current, [id]: ready }));
  }, []);

  const hiddenIds = dismissedIds;
  const ordered = plan.checks.flatMap((item) => {
    const result = results.find(
      (candidate) => candidate.kind === item.kind && candidate.photoId === item.photoId,
    );
    return result && !hiddenIds.includes(result.id) ? [result] : [];
  });

  function fieldsFor(result: HardwareCheck): string[] {
    const row = readingRow(hardware, result);
    const siblings = ordered.filter(
      (other) =>
        other.id !== result.id && other.kind === result.kind && other.index === result.index,
    );
    return selectedHardwareFields(
      hardwareReadingTicks(result, row, siblings),
      overrides[result.id] ?? {},
    );
  }

  function offerView(result: HardwareCheck) {
    return inverterIsolatorOfferView({
      hardware,
      check: result,
      target: isolatorTargets[result.id],
      dcIsolatorChecks: ordered.filter((other) => other.kind === 'dcIsolators'),
      otherInverterChecks: ordered.filter(
        (other) => other.kind === 'inverters' && other.id !== result.id,
      ),
    });
  }

  function isolatorApplication(result: HardwareCheck) {
    const view = offerView(result);
    if (view.index === undefined) return undefined;
    const fields = selectedInverterIsolatorFields(view.offers, isolatorOverrides[result.id] ?? {});
    return fields.length ? { index: view.index, fields } : undefined;
  }

  function isCurrent(result: HardwareCheck): boolean {
    const photo = photos.find((item) => item.photoId === result.photoId);
    return hardwareCheckResultCurrent(result, {
      hardware,
      submittedAt: report.submittedAt,
      photo: photo ? { photoId: photo.photoId, uploadedAt: photo.uploadedAt } : undefined,
      crop: result.crop,
      acceptedRow: acceptedRows[`${result.kind}:${result.index}`],
    });
  }

  function applyResults(chosen: readonly HardwareCheck[]) {
    const applications = chosen
      .filter((result) => isCurrent(result) && imagesReady[result.id])
      .map((result) => {
        const dcIsolator = isolatorApplication(result);
        return { result, fields: fieldsFor(result), ...(dcIsolator ? { dcIsolator } : {}) };
      })
      .filter((item) => item.fields.length > 0 || !!item.dcIsolator);
    if (!applications.length) return;
    try {
      const applied = applyHardwareCheckSelections(hardware, applications);
      onApply(applied.hardware, applied.selections);
      setAcceptedRows((current) => ({
        ...current,
        ...Object.fromEntries(
          applied.selections.map((selection) => [
            `${selection.kind}:${selection.index}`,
            selection.expectedRow,
          ]),
        ),
      }));
      // Leave unread/unselected fields available on this and sibling photos of the same unit.
      setOverrides((current) => ({
        ...current,
        ...Object.fromEntries(
          applications.map((application) => [
            application.result.id,
            Object.fromEntries(
              Object.entries(current[application.result.id] ?? {}).filter(
                ([field]) => !application.fields.includes(field),
              ),
            ),
          ]),
        ),
      }));
      setApplyError(null);
    } catch (error) {
      setApplyError(
        error instanceof Error &&
          (error.message === 'Hardware row no longer exists' ||
            error.message === 'Invalid hardware reading' ||
            error.message === 'DC isolator does not take inverter ratings')
          ? error.message
          : 'Could not apply those readings.',
      );
    }
  }

  const pendingReviews = pendingAcceptOnlySuggestions(
    ordered.filter((result) => isCurrent(result)),
    hardware,
    overrides,
  );
  const pendingKey = acceptOnlyReviewsKey(pendingReviews);
  useEffect(() => {
    onAcceptOnlyReviews?.(pendingReviews);
  }, [pendingKey, onAcceptOnlyReviews]);

  const readyCount = ordered.filter(
    (result) =>
      isCurrent(result) &&
      imagesReady[result.id] &&
      (fieldsFor(result).length > 0 || !!isolatorApplication(result)),
  ).length;
  const seen = new Set<string>([
    ...results.map((result) => checkKey(result)),
    ...failures.map((failure) => failure.key),
  ]);
  const unstarted: PlannedHardwareCheck[] =
    phase === 'done' ? plan.checks.filter((item) => !seen.has(checkKey(item))) : [];
  const multipleRows = plan.skipped.filter(
    (skip): skip is Extract<SkippedHardwareCheck, { reason: 'multiple-rows' }> =>
      skip.reason === 'multiple-rows',
  );
  const overCap = plan.skipped.filter(
    (skip): skip is Extract<SkippedHardwareCheck, { reason: 'over-cap' }> =>
      skip.reason === 'over-cap',
  );

  if (!allowed) {
    if (plan.checks.length === 0 && multipleRows.length === 0) return null;
    return <p className="muted">Automatic photo checks stay off for uploaded or closed reports.</p>;
  }
  if (plan.checks.length === 0 && plan.skipped.length === 0) return null;

  return (
    <div aria-label="Automatic hardware photo checks">
      {phase === 'checking' && (
        <p role="status">{autoCheckProgressLabel(done, plan.checks.length)}</p>
      )}
      {stoppedMessage && <p role="status">{stoppedMessage}</p>}
      {applyError && (
        <p role="status" className="error">
          {applyError}
        </p>
      )}
      {multipleRows.map((skip) => (
        <p key={skip.kind}>
          {HARDWARE_CHECK_KINDS[skip.kind].label}: {CHOOSE_PHOTO_FOR_EACH_UNIT}
        </p>
      ))}
      {[...overCap, ...unstarted].map((item) => (
        <p key={checkKey(item)}>
          {photoName(photos, item.photoId)}: {NOT_CHECKED_AUTOMATICALLY}
        </p>
      ))}
      {failures.map((failure) => (
        <p key={failure.key} role="status">
          {photoName(photos, failure.key.split(':').slice(2).join(':'))}: {failure.message}
        </p>
      ))}
      {ordered.map((result) => {
        const photo = photos.find((item) => item.photoId === result.photoId);
        const current = isCurrent(result);
        const siblings = ordered.filter(
          (other) =>
            other.id !== result.id && other.kind === result.kind && other.index === result.index,
        );
        const fields = fieldsFor(result);
        const view = offerView(result);
        const isolatorFields =
          view.index === undefined
            ? []
            : selectedInverterIsolatorFields(view.offers, isolatorOverrides[result.id] ?? {});
        return (
          <AutoCheckCard
            key={result.id}
            result={result}
            filename={photo?.filename ?? 'Photo'}
            photoPromptKey={photo?.promptKey}
            row={readingRow(hardware, result)}
            siblings={siblings}
            overrides={overrides[result.id] ?? {}}
            disabled={disabled}
            current={current}
            fieldCount={fields.length + isolatorFields.length}
            isolatorOffers={
              result.kind === 'inverters' ? (
                <InverterIsolatorOffers
                  view={view}
                  overrides={isolatorOverrides[result.id] ?? {}}
                  disabled={disabled}
                  onIsolatorIndex={(index) =>
                    setIsolatorTargets((currentTargets) => {
                      const next = { ...currentTargets };
                      if (index === undefined) delete next[result.id];
                      else next[result.id] = index;
                      return next;
                    })
                  }
                  onToggle={(field, checked) =>
                    setIsolatorOverrides((currentOverrides) => ({
                      ...currentOverrides,
                      [result.id]: { ...currentOverrides[result.id], [field]: checked },
                    }))
                  }
                />
              ) : null
            }
            onToggle={(field, checked) =>
              setOverrides((currentOverrides) => ({
                ...currentOverrides,
                [result.id]: { ...currentOverrides[result.id], [field]: checked },
              }))
            }
            onImageReady={onImageReady}
            onApply={() => applyResults([result])}
            onDismiss={() => onDismissedIdsChange?.(dismissHardwareReview(dismissedIds, result.id))}
          />
        );
      })}
      {dismissedIds.length > 0 && (
        <div className="hardware-photo-check__actions">
          <button
            type="button"
            disabled={disabled}
            onClick={() => onDismissedIdsChange?.(undoHardwareReviewDismiss(dismissedIds))}
          >
            Undo dismiss{dismissedIds.length > 1 ? ` (${dismissedIds.length})` : ''}
          </button>
        </div>
      )}
      {readyCount > 0 && (
        <div className="hardware-photo-check__actions">
          <button
            type="button"
            disabled={disabled || readyCount === 0}
            onClick={() => applyResults(ordered)}
          >
            Apply all ticked ({readyCount})
          </button>
        </div>
      )}
    </div>
  );
}

function AutoCheckCard({
  result,
  filename,
  row,
  siblings,
  overrides,
  disabled,
  current,
  fieldCount,
  photoPromptKey,
  isolatorOffers = null,
  onToggle,
  onImageReady,
  onApply,
  onDismiss,
}: {
  result: HardwareCheck;
  filename: string;
  row: Record<string, unknown> | undefined;
  siblings: readonly HardwareCheck[];
  overrides: Readonly<Record<string, boolean>>;
  disabled: boolean;
  current: boolean;
  fieldCount: number;
  photoPromptKey?: string | undefined;
  isolatorOffers?: ReactNode;
  onToggle: (field: string, checked: boolean) => void;
  onImageReady: (id: string, ready: boolean) => void;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const [image, setImage] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [imageError, setImageError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let url: string | undefined;
    void fetchPhotoBlobUrl(result.jobId, result.photoId)
      .then((value) => {
        url = value;
        if (!cancelled) setImage(value);
        else if (value.startsWith('blob:')) URL.revokeObjectURL(value);
      })
      .catch(() => {
        if (!cancelled) setImageError('Could not load the photo. Use Check photo below to retry.');
      });
    return () => {
      cancelled = true;
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [result.jobId, result.photoId, result.photoUploadedAt]);
  useEffect(() => {
    onImageReady(result.id, imageLoaded);
  }, [imageLoaded, onImageReady, result.id]);

  return (
    <article className="hardware-photo-check__auto" aria-label={`${filename} photo check`}>
      <p>
        <strong>
          {result.surface === 'switchboard'
            ? 'Switchboard'
            : `${HARDWARE_CHECK_KINDS[result.kind].label} ${result.index + 1}`}{' '}
          — {filename}
        </strong>
      </p>
      {imageError && <p role="status">{imageError}</p>}
      <div className="label-suggestions__review">
        {image && (
          <div>
            <label>
              Photo zoom
              <input
                aria-label={`Zoom ${filename}`}
                type="range"
                min="1"
                max="4"
                step="0.25"
                value={zoom}
                onChange={(event) => setZoom(Number(event.target.value))}
              />
            </label>
            <LabelPhotoCrop
              image={image}
              zoom={zoom}
              crop={undefined}
              selecting={false}
              onCrop={() => undefined}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageLoaded(false);
                setImageError('Could not display the photo. Use Check photo below to retry.');
              }}
            />
          </div>
        )}
        <div>
          {!current && (
            <p role="status">
              Hardware or photo changed. Check the photo again for an up-to-date comparison.
            </p>
          )}
          {current && (
            <>
              {result.needsClearerPhoto && (
                <p>A clearer label photo may help with unreadable details.</p>
              )}
              <HardwareCheckFields
                result={result}
                row={row}
                siblings={siblings}
                overrides={overrides}
                disabled={disabled}
                photoPromptKey={photoPromptKey}
                onToggle={onToggle}
              />
              {isolatorOffers}
              <button
                type="button"
                disabled={disabled || !imageLoaded || fieldCount === 0}
                onClick={onApply}
              >
                Apply selected to draft{fieldCount ? ` (${fieldCount})` : ''}
              </button>
            </>
          )}
          <button type="button" disabled={disabled} onClick={onDismiss}>
            Dismiss
          </button>
        </div>
      </div>
    </article>
  );
}
